import { Component, OnDestroy, OnInit } from '@angular/core';
import { AsyncSubject, interval, Subscription } from 'rxjs';
import { ContextChange, HttpService } from '@sdbn/appclient';
import { TMCClientParameters } from 'src/tmc-common/tmc-client-parameters';
import { TranslationService } from '@sdbn/common';
import { ComponentSelectionStorageService } from 'src/tmc-common/services/component-selection-storage.service';
import { environment } from 'src/environments/environment';
import { Transport, TransportIdentity, TransportStatus } from '../../model';
import { CustomFilter, CustomFilterType } from 'src/tmc-common/model/custom-filter';
import { AbstractWidgetComponent } from '@sdbn/appclient';
import { TransportFormatterService } from 'src/tmc-yard/legacy/transport-formatter-service';
import { ConfigurationService } from '@sdbn/appclient';
import { LayoutDefinition, TransportDetails } from 'src/tmc-yard/components/transport-details/transport-details-model';
import { TransportScope, TransportSelection } from 'src/tmc-yard/common/model/transport.model';
import { TransportDetailLayout, TransportDetailMode } from 'src/tmc-yard/common/configuration/transport.configuration';
import { TransportDetailsLayoutConverter } from 'src/tmc-yard/common/components/tmp/transport-details-layout-converter.service';
import { Transport as TransportNeu } from "src/tmc-yard/common/model/transport.model"
import { map } from 'rxjs/operators';
import { TmcContextService } from 'src/tmc-common/services/tmc-context.service';

const TABLE_SELECTION_KEY = 'app-transport-tile-list.table.selection';
const TABLE_SORT_KEY = 'app-transport-tile-list.table.sort';

@Component({
  selector: 'app-transport-tile-list',
  templateUrl: './transport-tile-list.component.html',
  styleUrls: ['./transport-tile-list.component.scss']
})
export class TransportTileListComponent extends AbstractWidgetComponent implements OnInit, OnDestroy {

  TransportDetailMode = TransportDetailMode;

  initialized = false;

  layoutDefinition: TransportDetailLayout;
  transports = new Array<Transport>();
  transportsForView = new Array<Transport>();
  selectedTransportId: number;

  refreshEnabled = true;
  refreshInterval = 30000;
  refreshTimer: Subscription;

  lastTransportChangedTimestamp: number;

  sortEmptyValuestoEndForColumns: string[] = ['start'];
  sortDirection = 'asc';
  sortColumn = 'start';

  totalCount = 0;
  plannedCount = 0;
  inProgressCount = 0;
  finishedCount = 0;

  filters: Array<CustomFilter> = null;
  widgetConfigurationFilterModel: TransportSelection;
  filterModel = new TransportSelection();
  filterSubscription: Subscription;

  constructor(
    private transportFormatterService: TransportFormatterService,
    private componentSelectionStorageService: ComponentSelectionStorageService,
    private configurationService: ConfigurationService,
    public translationService: TranslationService,
    private transportDetailsLayoutConverter : TransportDetailsLayoutConverter,
    private http: HttpService,
    private tmcContextService: TmcContextService
  ) {

    super();

    this.filterSubscription = this.tmcContextService.updated.subscribe(next => {
      if (next != null) {
        this.filters = this.tmcContextService.getYardConfiguration().customFilters.filter(f => f.type === CustomFilterType.TRANSPORT_LIST);
        this.initializeComponent();
      }
    });

    this.registerRefreshTimer();
  }

  ngOnInit() {
    this.restoreTableSort();
  }

  ngOnDestroy() {

    super.ngOnDestroy();

    if (this.refreshTimer) {
      this.refreshTimer.unsubscribe();
    }
    if (this.filterSubscription) {
      this.filterSubscription.unsubscribe();
    }
  }

  private registerRefreshTimer() {

    if (this.refreshTimer) {
      this.refreshTimer.unsubscribe();
    }

    if (environment.autoreload && this.refreshEnabled) {
      this.refreshTimer = interval(this.refreshInterval).subscribe(() => {
        this.loadTransports().subscribe(
          () => {
            this.restoreTableSelection();
          }
        );
      }
      );
    } else {
      console.log('Autoreload visits disabled');
    }
  }

  public loadTransports(): AsyncSubject<void> {
    const subject = new AsyncSubject<void>();
    const identity = new TransportIdentity();
    if (this.filterModel != null && this.filterModel.customFilterId !== -1) {
      const selectedFilter = this.filters.find(f => f.id === this.filterModel.customFilterId);
      identity.customFilterId = selectedFilter.id;
    }

    this.http.postWithContext<Array<Transport>>(`${environment.transportOverviewEndpoint}/transports`, identity).pipe(map(t => t.map(tr => new Transport(tr)))).subscribe(
      response => {
        this.mergeResponseIntoTransports(response);
        this.updateVisibleTransportsWithFilter();
        this.sortVisibleTransports();
        subject.next();
        subject.complete();
      },
      error => {
        this.transports = new Array<Transport>();
        console.log('Error wihle Loading list of vists!', error);
        subject.error(error);
      });
    return subject;
  }

  private mergeResponseIntoTransports(response: Transport[]) {

    const localTransportsById = new Map<number, Transport>();
    for (const localTransport of this.transports) {
      localTransportsById.set(localTransport.id, localTransport);
    }

    // Aktualisierte Transporte inklusive TransportDetails erzeugen
    for (const t of response) {
      this.updateTransportDetails(t);

      const localTransport = localTransportsById.get(t.id);
      if (localTransport != null) {
        // merge
        this.mergeTransport(t, localTransport);
        this.updateTransportDetails(localTransport);
        // gemergede transporte entfernen damit sie nicht aus der Liste entfernt werden.
        localTransportsById.delete(t.id);
      } else {
        this.transports.push(t);
      }
    }

    for (const transportId of localTransportsById.keys()) {
      this.transports.splice(this.transports.findIndex(t => t.id === transportId), 1);
    }
  }

  private mergeTransport(source: Transport, target: Transport) {
    target.name = source.name;
    target.carrier = source.carrier;
    target.supplier = source.supplier;
    target.numberPlate = source.numberPlate;
    target.reference = source.reference;
    target.receiver = source.receiver;
    target.start = source.start;
    target.startSortValue = source.startSortValue;
    target.end = source.end;
    target.urgent = source.urgent;
    target.status = source.status;
    target.currentStation = source.currentStation;
    target.nextStation = source.nextStation;
    target.waitingTime = source.waitingTime;
    target.activeMessagingChannels = source.activeMessagingChannels;
    target.messagingReference = source.messagingReference;
    target.highlightingStyle = source.highlightingStyle;
    target.processingTime = source.processingTime;
    target.registrationCode = source.registrationCode;
    target.processingTimeStyle = source.processingTimeStyle;
    target.searchText = source.searchText;
    target.identifier = source.identifier;
    target.transportScope = source.transportScope;
    target.options = source.options;
    target.cargo = source.cargo;
    target.planningDate = source.planningDate;
    target.planningDateSortValue = source.planningDateSortValue;
    target.messagingResources = source.messagingResources;
    target.string2 = source.string2;
    target.umbrueckung = source.umbrueckung;
    target.currentStationId = source.currentStationId;
    target.wasRegistered = source.wasRegistered;
    target.internal = source.internal;
    target.currentPositionStatusDictionaryKey = source.currentPositionStatusDictionaryKey;
    target.currentPositionStart = source.currentPositionStart;
    target.currentPositionStatusStart = source.currentPositionStatusStart;
    target.currentPositionDuration = source.currentPositionDuration;
    target.currentPositionStatusDuration = source.currentPositionStatusDuration;
    target.currentPositionStartSortValue = source.currentPositionStartSortValue;
    target.currentPositionStatusStartSortValue = source.currentPositionStatusStartSortValue;
    target.currentPositionCargo = source.currentPositionCargo;
    target.currentPositionHint = source.currentPositionHint;
    target.processingTimeCurrentProcessing = source.processingTimeCurrentProcessing;
    target.beginReal = source.beginReal;
    target.beginRealSortValue = source.beginRealSortValue;
    target.dangerousGoods = source.dangerousGoods;
  }

  private updateTransportDetails(transport: Transport) {

    if (transport.transportDetails == null) {
      transport.transportDetails = new TransportNeu();
    }

    transport.transportDetails.identifyingNumberplate = this.transportFormatterService.formatTransportIdentifier(
      transport.identifier, transport.transportScope);
    transport.transportDetails.masterdata.carrier = transport.carrier;
    transport.transportDetails.masterdata.supplier = transport.supplier;
    transport.transportDetails.masterdata.driverName = transport.name;
    transport.transportDetails.masterdata.coDriver = null;
    transport.transportDetails.masterdata.numberPlate = transport.numberPlate;
    transport.transportDetails.masterdata.firstLoadCarrier.trailerNumberPlate = null;
    transport.transportDetails.masterdata.firstLoadCarrier.swapTrailerNumber = null;
    transport.transportDetails.masterdata.firstLoadCarrier.orderReference = transport.reference;
    transport.transportDetails.masterdata.string1 = null;
    transport.transportDetails.masterdata.string2 = transport.string2;
    transport.transportDetails.masterdata.phone = null;
    transport.transportDetails.masterdata.identityCard = null;
    transport.transportDetails.masterdata.dataProtectionRuleId = null;
    transport.transportDetails.masterdata.urgent = transport.urgent;
    transport.transportDetails.masterdata.important = null;
    transport.transportDetails.masterdata.dangerousGoods = transport.dangerousGoods;
    transport.transportDetails.timing.planningType = null;
    transport.transportDetails.timing.inAdvanceDate = null;
    transport.transportDetails.masterdata.preloading = null;
    transport.transportDetails.masterdata.preloadingDate = null;
    transport.transportDetails.masterdata.delayedLeave = null;
    transport.transportDetails.masterdata.intermitted = null;
    // transport.transportDetails.registerNow = null;
    // transport.transportDetails.registerNowEnabled = null;
    transport.transportDetails.masterdata.pickupTruckEnabled = null;
    transport.transportDetails.masterdata.pickupTruckNumberPlate = null;
    transport.transportDetails.masterdata.pickupTruckTime = null;
    transport.transportDetails.messaging.useGlobalConnect = null;
    transport.transportDetails.messaging.useSms = null;
    transport.transportDetails.messaging.messagingLanguage = null;
    transport.transportDetails.timing.planningDate = transport.planningDate;
    transport.transportDetails.masterdata.firstLoadCarrier.cargo = transport.cargo;
    transport.transportDetails.transportOptionMarks = transport.options;
    transport.transportDetails.currentPosition.hint = transport.currentPositionHint;
    transport.transportDetails.currentPosition.cargo = transport.currentPositionCargo;
    transport.transportDetails.currentPosition.stationName = transport.currentStation;
    transport.transportDetails.timing.entranceDate = transport.start;
    transport.transportDetails.timing.waitingTime = transport.waitingTime;
    transport.transportDetails.processing.processingTime = transport.processingTime;
  }

  private restoreTableSort() {
    const tableSort = this.componentSelectionStorageService.getFromSession(this.widgetIdentifier + '.' + TABLE_SORT_KEY);
    if (tableSort != null) {
      this.sortColumn = tableSort.sortColumn;
      this.sortDirection = tableSort.sortDirection;
    }
  }

  private storeTableSort(sortColumn: string, sortDirection: string) {
    const filterId = this.filterModel?.customFilterId;
    this.componentSelectionStorageService.saveToSession(this.widgetIdentifier + '.' + TABLE_SORT_KEY,
      { filterId, sortColumn, sortDirection });
  }

  public toggleSelection(transport: Transport) {
    this.selectedTransportId = transport.id;
    this.storeTableSelection();
    if (this.selectedTransportId != null) {
      this.dashboardContext.setContextValue('selected-transport', this.transports.find(t => t.id === this.selectedTransportId));
    } else {
      this.dashboardContext.setContextValue('selected-transport', null);
    }
  }

  private storeTableSelection() {
    this.componentSelectionStorageService.saveToSession(this.widgetIdentifier + '.' + TABLE_SELECTION_KEY, this.selectedTransportId);
  }

  onContextChange(contextChange: ContextChange) {
    if (this.initialized && contextChange.key === 'transport-selection') {
      if (this.widgetConfigurationFilterModel == null) {
        this.refreshBySelection();
      }
    }

    if (contextChange.key === 'selected-transport') {
      if (contextChange.value == null) {
        this.selectedTransportId = null;
        this.storeTableSelection();
      } else if (this.selectedTransportId !== contextChange.value.id) {
        this.selectedTransportId = contextChange.value.id;
        this.storeTableSelection();
      }
    }

    if (this.initialized && contextChange.key === 'transport-changed') {
      if (this.lastTransportChangedTimestamp == null || this.lastTransportChangedTimestamp !== contextChange.value) {
        this.lastTransportChangedTimestamp = contextChange.value;
        this.reloadTransportList();
      }
    }
  }

  onSetElementAndContext() {
    this.initializeComponent();
  }

  private initializeComponent() {
    if (this.filters != null && this.widgetReference != null && this.dashboardContext != null) {

      if (this.widgetReference.data.transportLayout != null) {
        const layout = new LayoutDefinition(JSON.parse(this.widgetReference.data.transportLayout));
        layout.dictionaryBase = this.widgetReference.baseDictionaryKey;
        layout.propertyId = 'widget_transport_layout_' + this.widgetReference.identifier;
        this.layoutDefinition = this.transportDetailsLayoutConverter.convert(layout);
      }

      // Wenn Filter in der Widget Konfiguration gesetzt ist, dann wird dieser Initial genutzt!
      // Im Späteren Verlauf der Anwendung kann es möglich sein,
      // dass der Filter durch 'transport-selection' events auf dem Context überschrieben wird.
      // Beispielsweise von der TransportSelectionComponent im Dashboard.
      if (this.widgetReference.data.filter != null) {
        const filterOfWidgetReference = this.filters.find(f => f.externalKey === this.widgetReference.data.filter);
        if (filterOfWidgetReference != null) {
          const transportFilter = new TransportSelection();
          // transportFilter.filterExternalKey = filterOfWidgetReference.externalKey;
          // transportFilter.sortColumn = filterOfWidgetReference.sortColumn;
          // transportFilter.sortDescending = filterOfWidgetReference.sortDescending;
          // transportFilter.tableColumns = filterOfWidgetReference.tableColumns;

          this.widgetConfigurationFilterModel = transportFilter;
        }
      }

      this.refreshEnabled = this.widgetReference.refreshEnabled == null ? true : this.widgetReference.refreshEnabled;
      if (this.widgetReference.refreshInterval != null && this.widgetReference.refreshInterval > 0) {
        this.refreshInterval = this.widgetReference.refreshInterval * 1000;
      } else {
        this.refreshInterval = 30 * 1000;
      }
      this.registerRefreshTimer();

      this.refreshBySelection();
    }
    this.initialized = true;
  }

  private reloadTransportList() {
    this.loadTransports().subscribe(
      () => {
        this.restoreTableSelection();
      }
    );
  }

  private refreshBySelection() {

    const oldFilterModel = this.filterModel;

    const contextFilterModel: TransportSelection = this.dashboardContext?.context.get('transport-selection');

    if (this.widgetConfigurationFilterModel != null) {
      this.filterModel = this.widgetConfigurationFilterModel;
    } else if (contextFilterModel != null) {
      this.filterModel = contextFilterModel;
    } else {
      this.filterModel = null;
    }

    // wenn der gewählte Custom Filter sich nicht geändert hat
    if (this.filterModel != null && oldFilterModel != null && this.filterModel.customFilterId === oldFilterModel.customFilterId) {
      // only the datasource filter is triggered
      this.updateVisibleTransportsWithFilter();
      this.sortVisibleTransports();
    } else {
      this.loadTransports().subscribe(
        () => {
          this.restoreTableSelection();
        }
      );
    }

    // if (this.filterModel.resetTableSort) {
    //   if (this.filterModel.sortColumn != null) {
    //     this.sortColumn = this.filterModel.sortColumn;
    //   } else {
    //     this.sortColumn = 'start';
    //   }

    //   if (this.filterModel.sortDescending === true) {
    //     this.sortDirection = 'desc';
    //   } else {
    //     this.sortDirection = 'asc';
    //   }

    //   this.storeTableSort(this.sortColumn, this.sortDirection);
    //   this.updateVisibleTransportsWithFilter();
    //   this.sortVisibleTransports();
    // }
  }

  private restoreTableSelection() {
    const storedSelection: number = this.componentSelectionStorageService.getFromSession(
      this.widgetIdentifier + '.' + TABLE_SELECTION_KEY
    );

    this.selectedTransportId = storedSelection;
    this.dashboardContext.setContextValue('selected-transport', this.transports.find(t => t.id === this.selectedTransportId));
  }

  private updateVisibleTransportsWithFilter() {
    this.transportsForView = this.transports.filter(t => this.transportFilterPredicate(t));
    this.onVisibleRowsChanged();
  }

  private transportFilterPredicate(transport: Transport): boolean {

    if (this.filterModel == null) {
      return true;
    }

    let queryValid = false;
    let scopeValid = false;

    // wenn ein Query definiert ist dann dieses durchsuchen
    if (this.filterModel.filterText != null && this.filterModel.filterText !== '') {
      const normalizedFilterString = this.filterModel.filterText == null ? '' : this.filterModel.filterText.toLowerCase();
      for (const k of Object.keys(transport)) {
        let fieldValue = transport[k];
        // Für Transportstatus muss der überstezte Wert durchsucht werden.
        if (k === 'status') {
          fieldValue = this.translationService.translate('tmc.transport-status.' + transport[k]);
        }
        if (fieldValue != null) {
          const normalizedFieldValue = String(fieldValue).toLowerCase();
          if (normalizedFieldValue.indexOf(normalizedFilterString) > -1) {
            queryValid = true;
          }
        }
      }
    } else {
      queryValid = true;
    }

    if (this.filterModel.scopeTrain !== true && this.filterModel.scopeTrailer !== true && this.filterModel.scopeSwapTrailer !== true) {
      scopeValid = true;
    } else {
      scopeValid = (transport.transportScope === TransportScope.TRAIN && this.filterModel.scopeTrain) ||
        (transport.transportScope === TransportScope.TRAILER && this.filterModel.scopeTrailer) ||
        (transport.transportScope === TransportScope.SWAP_TRAILER && this.filterModel.scopeSwapTrailer);
    }

    return queryValid && scopeValid;
  }


  private sortVisibleTransports() {
    if (!this.sortColumn == null) {
      return;
    }
    const isAsc = this.sortDirection === 'asc';

    const emptyToEnd = this.sortEmptyValuestoEndForColumns != null
      && this.sortEmptyValuestoEndForColumns.indexOf(this.sortColumn) !== -1;

    this.transportsForView.sort((a: Transport, b: Transport) => {

      // dedicated sort value first
      let value1 = a[this.sortColumn + 'SortValue'];
      let value2 = b[this.sortColumn + 'SortValue'];

      // default if no sort value is available
      if (value1 == null || value2 == null) {
        value1 = a[this.sortColumn];
        value2 = b[this.sortColumn];
      }

      if (this.sortColumn === 'visitStatusDict') {
        value1 = this.translationService.translate('tmc.transport-status.' + a.status);
        value2 = this.translationService.translate('tmc.transport-status.' + b.status);
      }

      if (emptyToEnd) {
        // Treat null or empty values always as greater
        if ((value1 == null || value1 === '') && (value2 == null || value2 === '')) {
          return 0;
        }
        if (value1 == null || value1 === '') {
          return +1;
        }
        if (value2 == null || value2 === '') {
          return -1;
        }
      }
      return this.compare(value1, value2, isAsc);
    });
  }

  private compare(a: number | string, b: number | string, isAsc: boolean) {
    let t = 0;
    if ((a == null || a === '') && (b == null || b === '')) {
      t = 0;
    } else if (a == null || a === '') {
      t = -1;
    } else if (b == null || b === '') {
      t = 1;
    } else {
      t = (a < b ? -1 : 1);
    }
    return t * (isAsc ? 1 : -1);
  }

  private onVisibleRowsChanged() {

    const autoselectEnabled = this.configurationService.isEnabled(
      TMCClientParameters.P_VISITS_TRANSPORT_AUTO_SELECT_SINGLE_SEARCH_RESULT, false);

    if (autoselectEnabled && this.transportsForView != null && this.transportsForView.length === 1) {
      this.selectedTransportId = this.transportsForView[0].id;
    }

    this.totalCount = this.transportsForView.length;
    this.plannedCount = 0;
    this.inProgressCount = 0;
    this.finishedCount = 0;

    for (const r of this.transportsForView) {

      if (
        (r.planningDate != null && r.status === TransportStatus.PLANNED) ||
        r.status === TransportStatus.IN_THE_INFLOW
      ) {
        this.plannedCount += 1;
      } else if (
        r.status === TransportStatus.COMPLETE ||
        r.status === TransportStatus.FINISHED ||
        r.status === TransportStatus.CANCELLED ||
        r.status === TransportStatus.FINISHED_CANCELLED) {
        this.finishedCount += 1;
      } else if (
        r.status === TransportStatus.REGISTERED ||
        r.status === TransportStatus.RETRIEVED ||
        r.status === TransportStatus.WAITING ||
        r.status === TransportStatus.PAUSED
      ) {
        this.inProgressCount += 1;
      }
    }
  }
}
